import { useEffect, useState } from "react";
import { X, Lock, AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import {
  useResumoQuinzena, useFecharQuinzena, useAdicionaisDaQuinzena, useMensalistasDaQuinzena,
  useGerarAdiantamentos,
} from "@/hooks/use-equipe";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type { Quinzena } from "@shared/schema";
import {
  formatarResumoAdicional, paraReais,
  agruparAdicionaisPorFuncionario, totalAdicionaisCentavos as somarLinhasAdicional,
  totalAdiantamentosMensalistasCentavos, separarResumoQuinzena, rotuloTipoAdicional,
  separarMensalistasPorAdiantamento, competenciaDaQuinzena, rotuloSinalLancamento,
  adiantamentoSaiNestaQuinzena,
  lancamentosNoCaixaDaQuinzena,
} from "@shared/equipe-fechamento";

interface FecharQuinzenaSheetProps {
  open: boolean;
  quinzena: Quinzena;
  onClose: () => void;
  onSuccess: () => void;
}

/** Mensagem do servidor a partir do erro da requisição ("409: {json}"). */
function msgDoErro(e: unknown, fallback: string): string {
  const bruto = String((e as any)?.message ?? "");
  try {
    const j = JSON.parse(bruto.slice(bruto.indexOf(":") + 1).trim());
    if (typeof j?.error === "string") return j.error;
  } catch {
    // Corpo não-JSON: cai no fallback. Nunca engolir o erro em silêncio.
  }
  return fallback;
}

export function FecharQuinzenaSheet({ open, quinzena, onClose, onSuccess }: FecharQuinzenaSheetProps) {
  const { data: resumo, isLoading } = useResumoQuinzena(open ? quinzena.id : undefined);
  // Fase 3 — sem isto o gestor confirma o fechamento vendo um total menor do
  // que o que efetivamente vai sair na quinzena.
  const { data: adicionaisQuinzena } = useAdicionaisDaQuinzena(open ? quinzena.id : undefined);
  // §2 (v2.10) — MESMO hook que BlocoMensalistas.tsx/financeiro.tsx já usam pro
  // bloco "Mensalistas e encarregados" da tela principal. Não é query nova.
  const { data: mensalistas = [] } = useMensalistasDaQuinzena(open ? quinzena.id : undefined);
  const fechar = useFecharQuinzena();
  const gerarAdiantamentos = useGerarAdiantamentos();
  const { toast } = useToast();

  // Escape do bloqueio: fechar mesmo com mensalista sem adiantamento continua
  // possível (às vezes é legítimo), mas nunca com um clique só — o gestor digita
  // FECHAR. Em agosto a faixa âmbar avisava e o botão fechava do mesmo jeito.
  const [confirmacao, setConfirmacao] = useState("");
  useEffect(() => { if (!open) setConfirmacao(""); }, [open]);

  // §2.2 — uma linha por (pessoa, tipo): nunca soma COLHEITA e OUTRO juntos.
  // D-M4 (Financeiro v3) — desconto de MENSALISTA liquida no fechamento do mês,
  // nunca aqui: filtra ANTES de agrupar, senão a prévia abate duas vezes.
  const adicionalPorFuncionario = agruparAdicionaisPorFuncionario(
    lancamentosNoCaixaDaQuinzena(adicionaisQuinzena?.agregado ?? []),
  );

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const rows = Array.isArray(resumo) ? (resumo as any[]) : [];
  // Ver comentário equivalente em fechar-quinzena-dialog.tsx / shared/equipe-fechamento.ts
  // (`separarResumoQuinzena`) — mensalista é escopo à parte (seção própria
  // abaixo), nunca "sem lançamento" por causa da separação estrutural (§4.5).
  const mensalistaIds = new Set(mensalistas.map(m => m.id));
  const { comLancamento, semLancamento } = separarResumoQuinzena(
    rows, mensalistaIds, id => adicionalPorFuncionario.has(id),
  );
  const nOcultos = semLancamento.length;
  const totalAdicionaisCentavos = comLancamento.reduce(
    (acc: number, r: any) => acc + somarLinhasAdicional(adicionalPorFuncionario.get(r.id)), 0
  );
  const totalDiarias = comLancamento.reduce((acc: number, r: any) => acc + parseFloat(r.total_bruto || 0), 0);

  // Raiz comum do bug do #122: ausência de registro lida como ausência de
  // direito. `semAdiantamento` é quem É candidato (MENSALISTA + QUINZENAL)
  // mas ainda não tem adiantamento gerado nesta competência.
  // `comAdiantamento` = adiantamento da COMPETÊNCIA resolvido (pode ter saído na
  // OUTRA quinzena do mês). A prévia mostra e conta só o que sai NESTA — caixa.
  const { comAdiantamento, semAdiantamento } = separarMensalistasPorAdiantamento(mensalistas);
  const linhasMensalistas = comAdiantamento.filter(adiantamentoSaiNestaQuinzena);
  const totalMensalistasCentavos = totalAdiantamentosMensalistasCentavos(mensalistas);
  const nAdiantamentosAPagar = linhasMensalistas.filter(m => m.adiantamento_status === "PREVISTO").length;

  const totalGeral = totalDiarias + totalAdicionaisCentavos / 100 + totalMensalistasCentavos / 100;
  const totalDias = comLancamento.reduce((acc: number, r: any) => acc + parseFloat(r.dias_trabalhados || 0), 0);

  // Regra de não-regressão: sem pendência, fechar é o MESMO clique de hoje. A
  // trava só existe quando alguém realmente ficaria sem adiantamento.
  const temPendencia = !isLoading && semAdiantamento.length > 0;
  const bloqueado = temPendencia && confirmacao.trim().toUpperCase() !== "FECHAR";

  const fmtDias = (n: any) => {
    const v = parseFloat(String(n ?? 0));
    return Number.isInteger(v) ? String(v) : v.toFixed(1).replace(".", ",");
  };

  const handleFechar = async () => {
    try {
      await fechar.mutateAsync(quinzena.id);
      toast({ title: "Quinzena fechada!" });
      onSuccess();
      onClose();
    } catch (e) {
      // Mensagem REAL do servidor — "Erro ao fechar quinzena" genérico escondia
      // justamente os 409 que explicam o que precisa ser resolvido antes.
      toast({ title: msgDoErro(e, "Erro ao fechar quinzena"), variant: "destructive" });
    }
  };

  const handleGerarPendentes = async () => {
    try {
      await gerarAdiantamentos.mutateAsync({
        quinzenaId: quinzena.id,
        competencia: competenciaDaQuinzena(quinzena),
        itens: semAdiantamento.map(s => ({ funcionarioId: s.mensalista.id, valor: s.valorSugerido })),
      });
      toast({ title: "Adiantamentos gerados!" });
      // Não fecha o modal — a prévia atualiza sozinha (query invalidada pelo hook).
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao gerar adiantamentos"), variant: "destructive" });
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <div
        className={`fixed bottom-0 left-0 right-0 bg-card rounded-t-2xl z-50 transition-transform duration-300 ease-out shadow-xl flex flex-col max-h-[85vh] ${open ? "translate-y-0" : "translate-y-full"}`}
        role="dialog"
        aria-modal="true"
      >
        {/* Zona 1 — header fixo */}
        <div className="shrink-0 px-4 pt-3 pb-4 border-b">
          <div className="flex justify-center pb-2">
            <div className="w-9 h-1 rounded-full bg-border" />
          </div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold">Fechar quinzena</h2>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {format(new Date(quinzena.periodoInicio + "T00:00:00"), "dd/MM/yyyy")}
            {" – "}
            {format(new Date(quinzena.periodoFim + "T00:00:00"), "dd/MM/yyyy")}
          </p>
          <div className="flex items-center justify-between rounded-xl border border-sucesso/30 bg-sucesso-suave px-3 py-2">
            <span className="text-xs text-muted-foreground">{comLancamento.length + linhasMensalistas.length} funcionários · {fmtDias(totalDias)} dias</span>
            <span className="text-lg font-extrabold text-sucesso">R$ {totalGeral.toFixed(2)}</span>
          </div>
        </div>

        {/* Zona 2 — corpo rolável */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Carregando...</p>
          ) : (
            <div className="space-y-2">
              {comLancamento.map((r: any) => {
                const adicionais = adicionalPorFuncionario.get(r.id);
                const totalLinha = parseFloat(r.total_bruto) + somarLinhasAdicional(adicionais) / 100;
                return (
                  <div key={r.id} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground">{r.apelido ?? r.nome}</span>
                      <div className="text-right">
                        <span className="font-medium">R$ {totalLinha.toFixed(2)}</span>
                        <span className="text-muted-foreground ml-2 text-xs">
                          · {fmtDias(r.dias_trabalhados)} dia{parseFloat(r.dias_trabalhados) !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    {/* Desconto NUNCA pode aparecer com "+": no banco é o mesmo
                        valor positivo, com sentido oposto (migração 046). */}
                    {adicionais?.map(linha => {
                      const desconto = linha.sinal === "DESCONTO";
                      const cor = desconto ? "text-erro" : "text-sucesso";
                      return (
                        <div key={`${linha.tipo}-${linha.sinal}`} className="flex items-center justify-between text-xs pl-0.5">
                          <span className={cor}>
                            {rotuloTipoAdicional(linha.tipo)} — {rotuloSinalLancamento(linha.sinal).toLowerCase()}
                          </span>
                          <span className={`${cor} font-medium`}>
                            {desconto ? "−" : "+"} R$ {(linha.totalCentavos / 100).toFixed(2)}
                            <span className="text-muted-foreground font-normal ml-1">
                              ({formatarResumoAdicional({
                                qtd: linha.qtd, kgTotal: linha.kgTotal,
                                totalValor: paraReais(linha.totalCentavos),
                                tipoLabel: "lançamento",
                              })})
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* §2 (v2.10) — mensalistas/encarregados com adiantamento nesta
              quinzena. Valor é adiantamento (parte do salário), não diária. */}
          {!isLoading && linhasMensalistas.length > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Mensalistas e encarregados
              </h3>
              {linhasMensalistas.map(m => (
                <div key={m.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{m.apelido ?? m.nome}</span>
                  <div className="text-right">
                    <span className="font-medium">R$ {parseFloat(m.adiantamento_valor ?? "0").toFixed(2)}</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      adiantamento{m.adiantamento_status !== "PREVISTO" ? ` · ${m.adiantamento_status}` : ""}
                    </span>
                  </div>
                </div>
              ))}
              {nAdiantamentosAPagar > 0 && (
                <p className="text-[11px] text-muted-foreground pt-1">
                  {nAdiantamentosAPagar === 1
                    ? "O adiantamento acima será marcado como pago."
                    : `Os ${nAdiantamentosAPagar} adiantamentos acima serão marcados como pagos.`}
                </p>
              )}
            </div>
          )}

          {/* Raiz do bug: ausência de registro lida como ausência de direito
              (mesma classe do #122). A faixa era ÂMBAR e passiva — existia em
              agosto, foi lida, e o fechamento passou mesmo assim. Agora é
              checklist BLOQUEANTE: ou gera, ou digita FECHAR no rodapé. */}
          {!isLoading && semAdiantamento.length > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1.5">
              <div className="flex items-start gap-2 rounded-lg bg-erro-suave border-2 border-erro/30 text-erro text-xs p-2.5">
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  {semAdiantamento.length === 1
                    ? "1 mensalista sem adiantamento gerado nesta competência — vai receber só os adicionais, e o fechamento do mês pagaria o salário cheio."
                    : `${semAdiantamento.length} mensalistas sem adiantamento gerado nesta competência — vão receber só os adicionais, e o fechamento do mês pagaria o salário cheio.`}
                </span>
              </div>
              {semAdiantamento.map(({ mensalista: m, valorSugerido }) => (
                <div key={m.id} className="flex items-center justify-between text-sm pl-0.5">
                  <span className="text-foreground">{m.apelido ?? m.nome}</span>
                  <span className="font-medium text-atencao">R$ {parseFloat(valorSugerido).toFixed(2)}</span>
                </div>
              ))}
              <button
                type="button"
                onClick={handleGerarPendentes}
                disabled={gerarAdiantamentos.isPending}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-erro/30 bg-erro-suave hover:bg-erro/20 disabled:opacity-50 text-erro text-sm font-medium min-h-11 mt-1"
              >
                {gerarAdiantamentos.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {gerarAdiantamentos.isPending ? "Gerando..." : "Gerar agora"}
              </button>
            </div>
          )}
        </div>

        {/* Zona 3 — rodapé fixo */}
        <div className="shrink-0 px-4 pt-3 pb-8 border-t">
          <div className="flex items-start gap-2 bg-erro-suave border border-erro/30 rounded-xl p-3 mb-3 text-sm text-erro">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Após fechar, não será possível lançar nem editar diárias desta quinzena.</span>
          </div>

          {/* Confirmação digitada — só quando há pendência REAL. Sem pendência,
              fechar continua sendo o mesmo clique único de sempre. */}
          {temPendencia && (
            <div className="mb-3 space-y-1">
              <label className="text-xs text-erro font-medium">
                {semAdiantamento.length === 1
                  ? "1 mensalista ficará sem adiantamento."
                  : `${semAdiantamento.length} mensalistas ficarão sem adiantamento.`}
                {" "}Para fechar assim mesmo, digite <strong>FECHAR</strong>:
              </label>
              <input
                value={confirmacao}
                onChange={e => setConfirmacao(e.target.value)}
                placeholder="FECHAR"
                className="w-full rounded-lg border border-erro/30 px-3 min-h-11 text-sm uppercase tracking-wide"
                aria-label="Digite FECHAR para confirmar"
              />
            </div>
          )}

          <button
            onClick={handleFechar}
            disabled={fechar.isPending || isLoading || bloqueado}
            className="w-full flex items-center justify-center gap-2 bg-destructive hover:opacity-90 disabled:opacity-50 text-destructive-foreground rounded-xl py-3 font-medium transition-opacity"
          >
            <Lock className="h-4 w-4" />
            {fechar.isPending ? "Fechando..." : "Confirmar fechamento"}
          </button>
          {nOcultos > 0 && (
            <p className="text-xs text-center text-muted-foreground mt-2">{nOcultos} sem lançamento ocultado{nOcultos > 1 ? "s" : ""}</p>
          )}
        </div>
      </div>
    </>
  );
}
